/**
 * Kept work: a reload or an expired sign-in never loses what a person typed.
 *
 * A long-form page (a helper's form and its generated, edited document, a
 * note being written, a saved draft being edited) registers a keeper with
 * `useKeptWork(key, snapshot, restore)`. The snapshot is the whole form:
 * every field and setting, never one field alone. It is written to the
 * draft store (src/lib/draftAutosave.ts) a moment after each change, when
 * the page is hidden, and before the app sends the page to sign-in
 * (src/lib/api.ts authExpiry.redirectToLogin, on a write's expired sign-in
 * or a read that finds nobody signed in), under the store's own rules:
 *   - stamped with the signed-in person, never restored to anyone else,
 *     removed at sign-in when someone else signs in (claimDraftsFor);
 *   - discarded once older than 24 hours;
 *   - kept across a sign-out, so the same person signing back in has it.
 * The keeper's page reads it back on its next mount and shows
 * <KeptWorkNotice>. Only a successful save (clear) or Discard removes it;
 * after Discard nothing is written again until the work changes.
 *
 * Keys are `kept-work:<tenant>:<page>:<draft id, a client id or 'new'>`:
 * ids, never client content.
 *
 * A write that met the expired sign-in also leaves one content-free record
 * (`kept-work:report`: the route shape and the pages that kept work). After
 * the next sign-in the app shell sends it to the Errors screen as
 * `auth_expired_write` and removes it. A read leaves no record: nothing was
 * in flight.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTenant } from '@/lib/auth';
import { mintReference, reportPageCrash } from '@/lib/appErrors';
import { KEPT_WORK_PREFIX, readStoredDraft, writeStoredDraft } from '@/lib/draftAutosave';
import { pageUserId } from '@/lib/pageRestoreGuard';

export { KEPT_WORK_PREFIX };

export const KEPT_WORK_REPORT_KEY = `${KEPT_WORK_PREFIX}:report`;

export function keptWorkKey(tenantSlug: string, page: string, id: string | null): string {
  return `${KEPT_WORK_PREFIX}:${tenantSlug}:${page}:${id ?? 'new'}`;
}

interface Keeper {
  key: string;
  snapshot: () => unknown;
  authorId: string | null;
}

const keepers = new Set<Keeper>();

/** How long after a change the work is written. */
export const KEPT_WORK_SAVE_DELAY_MS = 300;

let pagehideInstalled = false;
function installPagehideFlush(): void {
  if (pagehideInstalled || typeof window === 'undefined') return;
  pagehideInstalled = true;
  window.addEventListener('pagehide', () => keepAllUnsavedWork(null));
}

/** The page part of a key: `kept-work:<tenant>:<page>:<id>` -> page. */
function pageOf(key: string): string {
  return key.split(':')[2] ?? 'unknown';
}

/** A route with every id taken out: no query, uuids and numbers as {id}. */
function routeShape(route: string): string {
  const path = (route.includes(' ') ? route.slice(route.indexOf(' ') + 1) : route).split('?')[0]!.trim();
  const shaped = path
    .split('/')
    .map((seg) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) || /^\d+$/.test(seg) ? '{id}' : seg,
    )
    .join('/');
  return shaped.startsWith('/') ? shaped : `/${shaped}`;
}

/**
 * Write every keeper's unsaved work to the draft store, stamped with its
 * person. `route` is the API route of the write that met the expired
 * sign-in, or null for a read. A keeper whose person is unknown writes
 * nothing: an unstamped draft would be claimed by whoever signs in next.
 */
export function keepAllUnsavedWork(route: string | null): void {
  const pages: string[] = [];
  let author: string | null = null;
  for (const keeper of keepers) {
    if (keeper.authorId === null) continue;
    let work: unknown;
    try {
      work = keeper.snapshot();
    } catch {
      continue;
    }
    if (work === null || work === undefined) continue;
    writeStoredDraft(keeper.key, JSON.stringify(work), keeper.authorId);
    pages.push(pageOf(keeper.key));
    author = keeper.authorId;
  }
  if (route === null) return;
  const who = author ?? pageUserId();
  if (who === null) return;
  writeStoredDraft(KEPT_WORK_REPORT_KEY, JSON.stringify({ route: routeShape(route), pages }), who);
}

/** The work kept under `key` for `authorId`, or null. */
export function readKeptWork<T>(key: string, authorId: string | null): T | null {
  const text = readStoredDraft(key, authorId);
  if (text === '') return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    writeStoredDraft(key, '', null);
    return null;
  }
}

/** After sign-in: send the record of a write that met an expired sign-in,
 *  once, then remove it. Never another person's. */
export function flushKeptWorkReport(tenantSlug: string, authorId: string): void {
  const report = readKeptWork<{ route?: unknown }>(KEPT_WORK_REPORT_KEY, authorId);
  if (report === null) return;
  writeStoredDraft(KEPT_WORK_REPORT_KEY, '', null);
  reportPageCrash(tenantSlug, {
    reference: mintReference(),
    route: typeof report.route === 'string' && report.route.startsWith('/') ? report.route : '/',
    error_code: 'auth_expired_write',
    detail: null,
  });
}

export interface KeptWork {
  /** True once kept work was put back on this page, until it is saved or
   *  discarded. */
  restored: boolean;
  /** A successful save: the kept copy is no longer needed. */
  clear: () => void;
  /** The person dropped the kept copy. */
  discard: () => void;
}

/**
 * Register this page's unsaved work, and put back what an expired sign-in
 * kept. `snapshot` returns the work now (null when there is none);
 * `restore` puts a kept copy back. Both are read when they run, so they
 * may close over this render's state.
 */
export function useKeptWork<T>(key: string, snapshot: () => T | null, restore: (work: T) => void): KeptWork {
  const { principal } = useTenant();
  const authorId = principal?.userId ?? null;
  const snapshotRef = useRef(snapshot);
  const restoreRef = useRef(restore);
  useLayoutEffect(() => {
    snapshotRef.current = snapshot;
    restoreRef.current = restore;
  });
  const [restored, setRestored] = useState(false);
  // The work as last written: unchanged work is not written again.
  const lastWrittenRef = useRef<string | null>(null);
  // The work as it stood when discarded: never written again, by any path,
  // until the person changes something.
  const discardedRef = useRef<string | null>(null);
  /** The work to write now, or null. */
  const current = (): { work: T; text: string } | null => {
    let work: T | null;
    try {
      work = snapshotRef.current();
    } catch {
      return null;
    }
    if (work === null || work === undefined) return null;
    const text = JSON.stringify(work);
    if (text === discardedRef.current) return null;
    discardedRef.current = null;
    return { work, text };
  };
  const currentRef = useRef(current);
  currentRef.current = current;

  // Nothing is written until this page has asked for its kept copy: a
  // first render's empty form must never overwrite it.
  const readyRef = useRef(false);

  useEffect(() => {
    installPagehideFlush();
    const keeper: Keeper = { key, snapshot: () => currentRef.current()?.work ?? null, authorId };
    keepers.add(keeper);
    return () => {
      keepers.delete(keeper);
    };
  }, [key, authorId]);

  useEffect(() => {
    readyRef.current = false;
    lastWrittenRef.current = null;
    discardedRef.current = null;
    if (authorId === null) return;
    const work = readKeptWork<T>(key, authorId);
    if (work !== null) {
      lastWrittenRef.current = JSON.stringify(work);
      restoreRef.current(work);
      setRestored(true);
    }
    readyRef.current = true;
  }, [key, authorId]);

  // A moment after each change, the work as it stands.
  useEffect(() => {
    if (authorId === null || !readyRef.current) return undefined;
    const timer = setTimeout(() => {
      const now = currentRef.current();
      if (now === null || now.text === lastWrittenRef.current) return;
      lastWrittenRef.current = now.text;
      writeStoredDraft(key, now.text, authorId);
    }, KEPT_WORK_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  });

  const clear = useCallback(() => {
    writeStoredDraft(key, '', null);
    setRestored(false);
  }, [key]);

  const discard = useCallback(() => {
    writeStoredDraft(key, '', null);
    try {
      const work = snapshotRef.current();
      discardedRef.current = work === null || work === undefined ? null : JSON.stringify(work);
    } catch {
      discardedRef.current = null;
    }
    lastWrittenRef.current = null;
    setRestored(false);
  }, [key]);

  return { restored, clear, discard };
}
